import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Alert, AlertTitle, Snackbar } from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { ApiError, apiErrorEmitter } from '../api/axiosConfig';
import routes from '../routes';
import { useAuth } from './AuthContext';

interface NotificationContextType {
    showNotification: (message: string, severity?: 'error' | 'warning' | 'info' | 'success') => void;
    hideNotification: () => void;
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotification = () => {
    const context = useContext(NotificationContext);
    if (!context) {
        throw new Error('useNotification must be used within a NotificationProvider');
    }
    return context;
};

interface NotificationState {
    open: boolean;
    message: string;
    severity: 'error' | 'warning' | 'info' | 'success';
}

export const NotificationProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { setUser, setIsLoggingOut } = useAuth();
    const [notification, setNotification] = useState<NotificationState>({
        open: false,
        message: '',
        severity: 'info'
    });

    const showNotification = useCallback((message: string, severity: 'error' | 'warning' | 'info' | 'success' = 'info') => {
        setNotification({
            open: true,
            message,
            severity
        });
    }, []);

    const hideNotification = useCallback(() => {
        setNotification(prev => ({
            ...prev,
            open: false
        }));
    }, []);

    const handleApiError = useCallback((error: ApiError) => {
        switch (error.type) {
            case 'TOKEN_EXPIRED':
                // Immediately clear all user data and queries
                queryClient.cancelQueries();
                queryClient.clear();
                setUser(null);
                setIsLoggingOut(false);

                // Clear all localStorage items related to authentication
                localStorage.removeItem('user');
                localStorage.removeItem('lookLikeAnAccountUser');

                // Show notification and redirect immediately
                showNotification('Session expired. Please log in again.', 'error');
                navigate(routes.auth.signIn);
                break;

            case 'API_SUCCESS_STATUS_ERROR':
                showNotification(error.message, 'error');
                break;

            case 'NETWORK_ERROR':
            case 'GENERIC_ERROR':
                showNotification(error.message, 'error');
                break;

            default:
                showNotification('An unexpected error occurred', 'error');
        }
    }, [navigate, showNotification, queryClient, setUser, setIsLoggingOut]);

    useEffect(() => {
        const unsubscribe = apiErrorEmitter.subscribe(handleApiError);
        return unsubscribe;
    }, [handleApiError]);

    return (
        <NotificationContext.Provider value={{ showNotification, hideNotification }}>
            {children}

            <Snackbar
                open={notification.open}
                autoHideDuration={6000}
                onClose={hideNotification}
                anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
            >
                <Alert
                    onClose={hideNotification}
                    severity={notification.severity}
                    variant="filled"
                    sx={{ width: '100%' }}
                >
                    {notification.severity === 'error' && <AlertTitle>Error</AlertTitle>}
                    {notification.message}
                </Alert>
            </Snackbar>
        </NotificationContext.Provider>
    );
};
